// src/pages/DocumentsPage.js
import { navigateTo } from "../../framework/router.ts";
import { globalState } from "../../framework/globalState.ts";
import { DashboardDocumentHtml } from '../../components/skins/dashboard-document.ts';
import { ExpandableDirectory } from '../../components/directory-list.ts';
import { getDocuments, deleteSiteDocument } from '../../services/api.ts';
import { DocumentUploadModal } from '../../components/DocumentUploadModal.ts';

// --------------------------------------------------
// Helper: create a table row
// --------------------------------------------------
function createDocumentRow(doc) {
  const tr = document.createElement("tr");

  tr.innerHTML = `
    <td class="text-left">${doc.name}</td>
    <td class="text-left">${new Date(doc.createdAt).toLocaleDateString()}</td>
    <td class="text-left">${new Date(doc.updatedAt).toLocaleDateString()}</td>
    <td class="text-left">${doc.linkType || ''}</td>
    <td class="text-left">${doc.source || ''}</td>
    <td class="text-left">${doc.lastModifier || ''}</td>
    <td class="text-left">${doc.author || ''}</td>
    <td class="text-left">${doc.size || ''}</td>
    <td class="text-left">${doc.tags ? doc.tags.join(", ") : ''}</td>
    <td class="text-left">
      <a href="${doc.fileUrl}" target="_blank">
        <button class="btn-icon">
          <span class="iconify" data-icon="mdi:download"></span>
        </button>
      </a>
    </td>
    <td class="text-left">
      <button class="btn-icon btn-danger delete-btn" data-id="${doc._id}">
        <span class="iconify" data-icon="mdi:delete"></span>
      </button>
    </td>
  `;

  return tr;
}

// --------------------------------------------------
// Fetch documents from API
// --------------------------------------------------
async function fetchDocuments(siteId) {
  try {
    return await getDocuments(siteId);
  } catch (err) {
    console.error("Error fetching documents:", err);
    return [];
  }
}

// --------------------------------------------------
// Delete document
// --------------------------------------------------
async function deleteDocument(id, rowElement) {
  try {
    await deleteSiteDocument(id);

    // Remove row from table
    rowElement.remove();
  } catch (err) {
    console.error("Error deleting document:", err);
  }
}

// --------------------------------------------------
// Main Documents Page
// --------------------------------------------------
export default function DocumentsPage() {
  const container = document.createElement("div");
  container.classList.add("parent-container");
  container.innerHTML = new DashboardDocumentHtml().documentskin();

  const tbody = container.querySelector("tbody");
  const siteWithData = globalState.currentSiteWithData;
  const site = (siteWithData as any)?.site || siteWithData;
  const siteId = site._id; // assuming you track current site

  if (!siteId) {
    tbody.innerHTML = `<tr><td colspan="11" class="text-center">No site selected</td></tr>`;
    return container;
  }

  // Upload modal
  const uploadBtn = container.querySelector("#upload-doc-btn");
  const uploadModal = new DocumentUploadModal(siteId);
  uploadBtn?.addEventListener("click", () => uploadModal.open());

  // Fetch and render documents
  fetchDocuments(siteId).then(docs => {
    // Clear existing rows
    tbody.innerHTML = "";

    if (!docs || docs.length === 0) {
      // Display "No documents found" row
      const tr = document.createElement("tr");
      tr.innerHTML = `
        <td colspan="11" class="text-center">No documents found</td>
      `;
      tbody.appendChild(tr);
      return;
    }

    docs.forEach(doc => {
      const tr = createDocumentRow(doc);
      tbody.appendChild(tr);

      // Add delete button handler
      tr.querySelector(".delete-btn").addEventListener("click", () => {
        deleteDocument(doc._id, tr);
      });
    });
  });

   // Update table dynamically when a document is uploaded
  document.addEventListener("documentUploaded", (e) => {
    const tr = createDocumentRow(e.detail);
    tbody.appendChild(tr);
    tr.querySelector(".delete-btn").addEventListener("click", () => deleteDocument(e.detail._id, tr));
  });

  // Initialize ExpandableDirectory if needed
  requestAnimationFrame(() => {
    new ExpandableDirectory(".directory-list");
  });

  return container;
}
